/**
 * One radar for the whole comparison: an axis per evaluator (0–1) and a
 * polygon per experiment, in the series colours. Hand-drawn SVG like the
 * single-experiment radar (rings, axis names), with a legend and a tooltip
 * listing every experiment's score on the axis under the pointer. Needs three
 * or more evaluators to be a shape; with fewer, the Scores table says it all.
 */

import { useState } from "react";
import { wrapLabel } from "@/features/experiments/detail/radar-chart";
import { formatScore } from "@/lib/format";
import { colourOf } from "./selection";
import type { Comparison } from "./types";

// Sized for a ~400px column beside the Scores table, so it draws near 1:1.
const W = 380;
const H = 290;
const CX = W / 2;
const CY = H / 2 + 2;
const R = 84;
const RINGS = [0.25, 0.5, 0.75, 1];
const LINE = 14;

/** Three or more evaluators make a shape; with fewer, the Scores table says it all. */
export const hasRadar = (data: Comparison) => data.evaluators.length >= 3;

function at(i: number, n: number, r: number): [number, number] {
  const angle = -Math.PI / 2 + (2 * Math.PI * i) / n;
  return [CX + r * Math.cos(angle), CY + r * Math.sin(angle)];
}

function AxisLabel({ i, n, name }: { i: number; n: number; name: string }) {
  const [x, y] = at(i, n, R + 16);
  const lines = wrapLabel(name, 12);
  const anchor = Math.abs(x - CX) < 8 ? "middle" : x > CX ? "start" : "end";
  const extra = (lines.length - 1) * LINE;
  const startY = y < CY - R * 0.5 ? y - extra : y > CY + R * 0.5 ? y + 10 : y + 5 - extra / 2;
  return (
    <text textAnchor={anchor} className="fill-body text-[12px]" aria-hidden>
      {lines.map((line, k) => (
        <tspan key={k} x={x} y={startY + k * LINE}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

export function CompareRadar({ data }: { data: Comparison }) {
  const [hover, setHover] = useState<number | null>(null);
  const axes = data.evaluators;
  const n = axes.length;
  if (!hasRadar(data)) return null;
  const points = new Map(data.points.map((p) => [p.experiment_id, p]));
  const value = (expId: string, key: string) => points.get(expId)?.scores[key] ?? null;
  const ring = (r: number) => axes.map((_, i) => at(i, n, R * r).join(",")).join(" ");
  const anchor = hover === null ? null : at(hover, n, R);

  return (
    <section className="box flex flex-col gap-2 px-4 py-3.5" aria-label="Scores by evaluator">
      <h2 className="text-sm font-semibold">Scores by evaluator</h2>
      <ul aria-label="Legend" className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
        {data.experiments.map((e, i) => (
          <li key={e.id} className="inline-flex items-center gap-1.5">
            <span aria-hidden className="size-2.5 rounded-[3px]" style={{ background: colourOf(i) }} />
            {e.name}
          </li>
        ))}
      </ul>
      <div className="relative mx-auto w-full max-w-[420px]" onMouseLeave={() => setHover(null)}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={`Radar chart of ${n} evaluator scores for ${data.experiments.length} experiments`}
          className="h-auto w-full overflow-visible"
        >
          {RINGS.map((r) => (
            <polygon
              key={r}
              points={ring(r)}
              fill={r === 1 ? "var(--color-well)" : "none"}
              stroke="var(--color-line)"
              strokeWidth={1}
            />
          ))}
          {axes.map((_, i) => {
            const [x, y] = at(i, n, R);
            return <line key={i} x1={CX} y1={CY} x2={x} y2={y} stroke="var(--color-line-soft)" />;
          })}
          {RINGS.map((r) => (
            <text
              key={r}
              x={CX + 4}
              y={CY - R * r + 3}
              className="fill-dim font-mono text-[10px]"
              aria-hidden
            >
              {r}
            </text>
          ))}
          {data.experiments.map((e, s) => (
            <polygon
              key={e.id}
              points={axes
                .map((a, i) => at(i, n, R * Math.max(0, Math.min(1, value(e.id, a.key) ?? 0))).join(","))
                .join(" ")}
              fill={colourOf(s)}
              fillOpacity={0.1}
              stroke={colourOf(s)}
              strokeWidth={1.75}
              strokeLinejoin="round"
            />
          ))}
          {axes.map((a, i) => {
            const [x, y] = at(i, n, R);
            return (
              <line
                key={a.key}
                x1={CX}
                y1={CY}
                x2={x}
                y2={y}
                stroke="transparent"
                strokeWidth={26}
                onMouseEnter={() => setHover(i)}
              />
            );
          })}
          {axes.map((a, i) => (
            <AxisLabel key={a.key} i={i} n={n} name={a.name} />
          ))}
        </svg>
        {hover !== null && anchor && axes[hover] && (
          <div
            role="tooltip"
            className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-[calc(100%+10px)] rounded-[7px]
                       border border-input bg-surface px-2.5 py-1.5 text-xs whitespace-nowrap shadow-sm"
            style={{ left: `${(anchor[0] / W) * 100}%`, top: `${(anchor[1] / H) * 100}%` }}
          >
            <div className="font-semibold text-ink">{axes[hover].name}</div>
            {data.experiments.map((e, s) => (
              <div key={e.id} className="flex items-center gap-1.5 text-body">
                <span aria-hidden className="size-2 rounded-sm" style={{ background: colourOf(s) }} />
                {e.name} <span className="font-mono">{formatScore(value(e.id, axes[hover]!.key))}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
